import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Play, Pause, Check, BookOpen, Heart, Star, Music, Palette,
  ChevronRight, Volume2, VolumeX, Printer, Loader2, Trophy, RotateCcw, Gamepad2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import axios from 'axios';
import { motion } from 'framer-motion';
import VoiceSelector, { VOICE_OPTIONS } from '@/components/VoiceSelector';
import InteractiveWorksheet from '@/components/InteractiveWorksheet';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default activity videos (fallback if API fails) - Verified Saddleback Kids videos
const DEFAULT_ACTIVITY_VIDEOS = {
  1: { id: "teu7BCZTgDs", title: "Creation - God Makes Everything" },
  2: { id: "teu7BCZTgDs", title: "Adam and Eve - The Garden of Eden" },
  3: { id: "0rKC7ElkTUQ", title: "Adam and Eve Sin - The Fall" },
  4: { id: "ptv_SxDVDWs", title: "Cain and Abel - Bible Story for Kids" },
  5: { id: "qzYjy6lhRag", title: "Noah's Ark - The Great Flood" },
  6: { id: "ALrGM23GRoA", title: "God's Promise - The Rainbow" },
  7: { id: "kxVCoLrz0gU", title: "Tower of Babel - Bible Story" },
  8: { id: "PlT5o2jphPM", title: "Abraham's Call - Journey of Faith" },
  9: { id: "1P9G-YXXzGU", title: "Isaac is Born - God's Promise Fulfilled" },
  10: { id: "1lKq0-Z4B5w", title: "Jacob and Esau - Twin Brothers" },
};

// Story content with illustrations for each week (kid-friendly versions)
const STORY_CONTENT = {
  1: {
    title: "God Creates the World",
    illustration: "https://static.prod-images.emergentagent.com/jobs/7627078d-d828-49b2-9c35-092029e8c91d/images/f02c76286fabad5ce0261e24bd83e2f00668a5395d5f83033f9150e99e1af32b.png",
    story: `In the very beginning, there was nothing but darkness everywhere. Then God said, "Let there be light!" and suddenly, beautiful light appeared! God made the bright blue sky, the soft fluffy clouds, and all the sparkling stars in the night sky. He created the oceans with their splashing waves and the mountains that reach up so high. God made all the colorful flowers, the tall green trees, and the soft green grass. He created all the animals too - the lions that roar, the birds that sing, the fish that swim, and the butterflies with their pretty wings. Everything God made was wonderful and good! God is so powerful and creative, and He made this beautiful world for us to enjoy.`,
    funFact: "Did you know? God created everything in just 6 days, and on the 7th day, He rested!"
  },
  2: {
    title: "Adam and Eve",
    illustration: "https://static.prod-images.emergentagent.com/jobs/7627078d-d828-49b2-9c35-092029e8c91d/images/e5d96826110ffb519d657e68f24cc6790097453689fcb696dd9c87238437d3e4.png",
    story: `God made the very first people - a man named Adam and a woman named Eve. God formed Adam from the dust of the ground and breathed life into him. Then God made Eve to be Adam's special helper and friend. God put them in a beautiful garden called Eden, filled with yummy fruits and friendly animals. Adam and Eve were so happy! They could talk to God and walk with Him in the garden every day. God gave them everything they needed and loved them very much. God made each of us special too, just like He made Adam and Eve!`,
    funFact: "Did you know? Adam named all the animals in the garden!"
  },
  3: {
    title: "The Fall",
    illustration: "https://static.prod-images.emergentagent.com/jobs/7627078d-d828-49b2-9c35-092029e8c91d/images/03aa08a8d16f2af067c7dc932220be533cb9d1b04b2d40664e34d5d37a596f24.png",
    story: `In the beautiful Garden of Eden, there was one tree that God said Adam and Eve should not eat from. A sneaky snake came and told Eve that it was okay to eat the fruit. Eve listened to the snake instead of God, and she ate the fruit. She gave some to Adam, and he ate it too. This made God very sad because they didn't obey Him. Adam and Eve had to leave the beautiful garden. But even though they made a bad choice, God still loved them and promised to help them. This teaches us that our choices matter, and we should always try to listen to God.`,
    funFact: "Did you know? Even when we make mistakes, God still loves us and forgives us!"
  },
  4: {
    title: "Cain and Abel",
    illustration: "https://static.prod-images.emergentagent.com/jobs/7627078d-d828-49b2-9c35-092029e8c91d/images/682287294a91444b301acc9867312b06eaecc2ae7476264744100b9fea539c00.png",
    story: `Adam and Eve had two sons named Cain and Abel. Abel was a shepherd who took care of fluffy sheep. Cain was a farmer who grew fruits and vegetables. One day, both brothers brought gifts to God. Abel brought his very best lamb because he wanted to make God happy. Cain didn't bring his best - he just brought whatever was easy. God was pleased with Abel's gift because Abel gave with a happy, loving heart. This story teaches us that when we give to God, we should always give our very best, because God deserves our best!`,
    funFact: "Did you know? Giving our best shows God how much we love Him!"
  },
  5: {
    title: "Noah's Ark",
    illustration: "https://static.prod-images.emergentagent.com/jobs/7627078d-d828-49b2-9c35-092029e8c91d/images/4f7dadb72028a50fe369a4c4a367e1e7e85d4f185acdd082bf901b88b67a0041.png",
    story: `Noah was a good man who loved God. But the other people in the world had become very mean and forgot about God. God was sad about this, so He decided to send a big flood. But God wanted to save Noah and his family! God told Noah to build a huge boat called an ark. Noah worked hard for many, many years building the ark. Then God sent two of every kind of animal to Noah - lions, giraffes, elephants, rabbits, birds, and so many more! Noah, his family, and all the animals got on the ark just before the rain started. It rained and rained for 40 days and 40 nights! But everyone on the ark was safe because Noah obeyed God.`,
    funFact: "Did you know? Noah's ark was longer than a football field!"
  },
  6: {
    title: "God's Promise - The Rainbow",
    illustration: "https://static.prod-images.emergentagent.com/jobs/7627078d-d828-49b2-9c35-092029e8c91d/images/f2531fb3efc5526248037c5b15a4195b620074a760162125a92a1bd67d299949.png",
    story: `After the great flood, the water finally went away and Noah's ark landed on a mountain. Noah and his family and all the animals came out of the ark. They were so happy to see dry land again! Noah thanked God for keeping them safe. Then something amazing happened - God put a beautiful rainbow in the sky! The rainbow had all the colors - red, orange, yellow, green, blue, and purple. God said, "This rainbow is my promise to you. I will never flood the whole earth again." Every time we see a rainbow, we can remember that God always keeps His promises!`,
    funFact: "Did you know? Every rainbow has the same colors in the same order!"
  },
  7: {
    title: "Tower of Babel",
    illustration: "https://static.prod-images.emergentagent.com/jobs/7627078d-d828-49b2-9c35-092029e8c91d/images/d8abb85181f6a196ab59f9b580acfc3592b29f4e9faf93e7cd6a503fa69127ba.png",
    story: `After the flood, there were many people on the earth again. They all spoke the same language and could understand each other. But instead of spreading out like God wanted, they decided to build a tall, tall tower that would reach up to the sky. They wanted to make themselves famous instead of honoring God. God saw what they were doing and knew it wasn't good. So God made something amazing happen - suddenly, different groups of people started speaking different languages! They couldn't understand each other anymore, so they stopped building and moved to different places. This reminds us that we should always be humble and honor God.`,
    funFact: "Did you know? The word 'Babel' sounds like the word for 'confused'!"
  },
  8: {
    title: "Abraham's Call",
    illustration: "https://static.prod-images.emergentagent.com/jobs/7627078d-d828-49b2-9c35-092029e8c91d/images/0b9273bf7b089397e9a48445609aa27862db159104c73d6b44b4cca4aafb2bb2.png",
    story: `There was a man named Abraham who loved God very much. One day, God spoke to Abraham and said, "Leave your home and go to a new land that I will show you. I will bless you and make you a blessing to others!" This was a big adventure! Abraham didn't know exactly where he was going, but he trusted God. So Abraham packed up everything - his family, his tents, and his animals - and started walking. He followed wherever God led him. Abraham showed us what it means to trust God and follow Him, even when we don't know what's going to happen next!`,
    funFact: "Did you know? Abraham is called the 'Father of Faith' because he trusted God so much!"
  },
  9: {
    title: "Isaac is Born",
    illustration: "https://static.prod-images.emergentagent.com/jobs/7627078d-d828-49b2-9c35-092029e8c91d/images/1b2c5d376a0d43ab2c8a81cd4a8db774f569e0d544891e8f455546dca462130a.png",
    story: `God had promised Abraham that he would have a son, but Abraham and his wife Sarah waited for a very long time. They were very old - even older than grandparents! But God always keeps His promises. One day, three special visitors came to Abraham's tent. They told Abraham that Sarah would have a baby boy in one year. Sarah laughed because she thought she was too old! But guess what? God's promise came true! Baby Isaac was born, and his name means "laughter" because he brought so much joy. Abraham and Sarah were so happy! This teaches us that God's timing is always perfect.`,
    funFact: "Did you know? Abraham was 100 years old when Isaac was born!"
  },
  10: {
    title: "Jacob and Esau",
    illustration: "https://static.prod-images.emergentagent.com/jobs/7627078d-d828-49b2-9c35-092029e8c91d/images/d669a73afcdde78103583c4d88e42bd3350d3800637bd53634b2735e9ee17954.png",
    story: `Isaac grew up and had twin boys named Jacob and Esau. Even though they were twins, they were very different! Esau was born first and had lots of red hair. He loved to hunt and be outdoors. Jacob was quieter and liked to stay near the tents. One day, Esau came home very hungry from hunting. Jacob was cooking some yummy soup. Esau said he would trade his special birthright (the blessing for the oldest son) just for some soup! This wasn't a good choice. This story teaches us that we should value the special things God gives us and not trade them away for things that don't last.`,
    funFact: "Did you know? Jacob's name means 'heel grabber' because he was born holding Esau's heel!"
  }
};

const ChristianLesson = () => {
  const { childId, weekNumber } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [lesson, setLesson] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [completing, setCompleting] = useState(false);
  
  // Read to Me state
  const [isReading, setIsReading] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);
  const [speechRate, setSpeechRate] = useState(0.85);
  const [selectedVoice, setSelectedVoice] = useState('shimmer'); // User-selectable voice
  const utteranceRef = useRef(null);
  const weekNum = parseInt(weekNumber) || 1;

  // Activity state
  const [downloadingWorksheet, setDownloadingWorksheet] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [activityVideos, setActivityVideos] = useState(DEFAULT_ACTIVITY_VIDEOS);
  const [showInteractiveWorksheet, setShowInteractiveWorksheet] = useState(false);
  const [worksheetCompleted, setWorksheeetCompleted] = useState(false);

  // Create an api helper for InteractiveWorksheet
  const apiHelper = {
    get: (url, config) => axios.get(`${API_URL}/api${url}`, {
      ...config,
      headers: { ...(config?.headers || {}), Authorization: `Bearer ${localStorage.getItem('littlelearners-token') || localStorage.getItem('token')}` }
    }),
    post: (url, data, config) => axios.post(`${API_URL}/api${url}`, data, {
      ...config,
      headers: { ...(config?.headers || {}), Authorization: `Bearer ${localStorage.getItem('littlelearners-token') || localStorage.getItem('token')}` }
    }),
  };

  const steps = [
    { id: 'story', name: 'Bible Story', icon: BookOpen },
    { id: 'verse', name: 'Memory Verse', icon: Heart },
    { id: 'activity', name: 'Activity', icon: Palette },
    { id: 'character', name: 'Character Lesson', icon: Star },
    { id: 'complete', name: 'Complete!', icon: Trophy }
  ];

  useEffect(() => {
    fetchLesson();
    fetchActivityVideos();
  }, [weekNumber]);

  const fetchLesson = async () => {
    try {
      const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/christian/curriculum/week/${weekNumber}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setLesson(response.data);
    } catch (error) {
      console.error('Failed to fetch lesson:', error);
      toast.error('Failed to load lesson');
    } finally {
      setLoading(false);
    }
  };

  // Fetch activity videos from backend (with health-checked URLs)
  const fetchActivityVideos = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/christian/activity-videos`);
      if (response.data.videos) {
        setActivityVideos(response.data.videos);
      }
    } catch (error) {
      console.error('Failed to fetch activity videos, using defaults:', error);
      // Keep using defaults
    }
  };

  // Get story content for current week
  const storyContent = STORY_CONTENT[weekNum] || STORY_CONTENT[1];

  // AI Text-to-Speech for natural reading
  const [audioElement, setAudioElement] = useState(null);
  const [ttsLoading, setTtsLoading] = useState(false);

  const readStoryWithAI = async () => {
    setTtsLoading(true);
    setIsReading(true);
    setReadingProgress(0);
    
    try {
      const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/christian/tts`,
        {
          text: storyContent.story,
          voice: selectedVoice, // User-selected voice
          speed: speechRate
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        // Create audio from base64
        const audioData = response.data.audio;
        const audioBlob = await fetch(`data:audio/mp3;base64,${audioData}`).then(r => r.blob());
        const audioUrl = URL.createObjectURL(audioBlob);
        
        const audio = new Audio(audioUrl);
        setAudioElement(audio);
        
        // Track progress
        audio.ontimeupdate = () => {
          if (audio.duration) {
            setReadingProgress((audio.currentTime / audio.duration) * 100);
          }
        };
        
        audio.onended = () => {
          setIsReading(false);
          setReadingProgress(100);
          toast.success('Story finished! Great listening!', { icon: '📖' });
        };
        
        audio.onerror = () => {
          setIsReading(false);
          toast.error('Audio playback error');
        };
        
        audio.play();
      }
    } catch (error) {
      console.error('TTS Error:', error);
      toast.error('Could not generate audio. Please try again.');
      setIsReading(false);
    } finally {
      setTtsLoading(false);
    }
  };

  // Enhanced Text-to-Speech for Read to Me (ElevenLabs only)
  const readStory = () => {
    readStoryWithAI();
  };

  const pauseReading = () => {
    if (audioElement) {
      audioElement.pause();
    }
    setIsReading(false);
  };

  const resumeReading = () => {
    if (audioElement) {
      audioElement.play();
    }
    setIsReading(true);
  };

  const stopReading = () => {
    if (audioElement) {
      audioElement.pause();
      audioElement.currentTime = 0;
      setAudioElement(null);
    }
    setIsReading(false);
    setReadingProgress(0);
  };

  // Download worksheet PDF
  const downloadWorksheet = async () => {
    setDownloadingWorksheet(true);
    try {
      const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/christian/worksheet/${weekNum}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          responseType: 'blob'
        }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `zazu_week${weekNum}_worksheet.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Worksheet downloaded! Print and enjoy!');
    } catch (error) {
      console.error('Failed to download worksheet:', error);
      toast.error('Failed to download worksheet. Please try again.');
    } finally {
      setDownloadingWorksheet(false);
    }
  };

  // Get activity video for current week (from DB-backed state)
  const activityVideo = activityVideos[weekNum] || activityVideos[1] || DEFAULT_ACTIVITY_VIDEOS[1];

  // AI-powered natural voice for short texts (verses, fun facts)
  const [shortTextSpeaking, setShortTextSpeaking] = useState(false);
  const shortAudioRef = useRef(null);

  const speakText = async (text) => {
    if (!text) return;
    
    // Stop any current audio
    if (shortAudioRef.current) {
      shortAudioRef.current.pause();
      shortAudioRef.current = null;
    }
    
    setShortTextSpeaking(true);
    
    try {
      const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/christian/tts`,
        { text, voice: selectedVoice, speed: speechRate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const audioData = response.data.audio;
        const audioBlob = await fetch(`data:audio/mp3;base64,${audioData}`).then(r => r.blob());
        const audioUrl = URL.createObjectURL(audioBlob);
        
        const audio = new Audio(audioUrl);
        shortAudioRef.current = audio;
        
        audio.onended = () => {
          setShortTextSpeaking(false);
          URL.revokeObjectURL(audioUrl);
        };
        
        audio.onerror = () => {
          setShortTextSpeaking(false);
        };
        
        audio.play();
      }
    } catch (error) {
      console.error('TTS error:', error);
      setShortTextSpeaking(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Stop any playing audio
      if (shortAudioRef.current) {
        try { shortAudioRef.current.pause(); } catch {}
      }
      if (audioElement) {
        try { audioElement.pause(); } catch {}
      }
    };
  }, [audioElement]);

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/christian/child/${childId}/complete-lesson?week_number=${weekNumber}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`+${response.data.xp_earned} XP! Lesson Complete!`);
        navigate(`/christian/child/${childId}`);
      }
    } catch (error) {
      toast.error('Failed to complete lesson');
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-indigo-950 to-purple-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
      </div>
    );
  }

  const weekData = lesson?.week;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-indigo-950 to-purple-950 pb-8">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-md border-b border-white/10 p-4 sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(`/christian/child/${childId}`)} className="text-purple-400">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-white">Week {weekNumber}</h1>
            <p className="text-xs text-purple-400">{lesson?.quarter?.replace('_', ' ')?.toUpperCase()}</p>
          </div>
          <Button variant="ghost" size="sm" className="text-slate-400" onClick={() => window.print()}>
            <Printer className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="px-4 py-4">
        <div className="flex items-center justify-between mb-2">
          {steps.map((step, idx) => (
            <div 
              key={step.id} 
              className={`flex flex-col items-center ${idx <= currentStep ? 'text-purple-400' : 'text-slate-600'}`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${
                idx < currentStep ? 'bg-emerald-500' : idx === currentStep ? 'bg-purple-500' : 'bg-slate-700'
              }`}>
                {idx < currentStep ? <Check className="w-4 h-4 text-white" /> : <step.icon className="w-4 h-4 text-white" />}
              </div>
              <span className="text-[10px]">{step.name}</span>
            </div>
          ))}
        </div>
        <Progress value={(currentStep / (steps.length - 1)) * 100} className="h-1" />
      </div>

      {/* Content */}
      <div className="px-4">
        {/* Bible Story Step */}
        {currentStep === 0 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            <Card className="bg-gradient-to-br from-indigo-900/50 to-purple-900/50 border-purple-500/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-purple-400" />
                  {storyContent.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Story Illustration */}
                {storyContent.illustration && (
                  <div className="rounded-2xl overflow-hidden border-2 border-purple-500/30 shadow-lg shadow-purple-500/20">
                    <img 
                      src={storyContent.illustration} 
                      alt={storyContent.title}
                      className="w-full h-auto"
                      loading="eager"
                    />
                  </div>
                )}

                {/* Read to Me Controls */}
                <Card className="bg-gradient-to-br from-emerald-900/40 to-teal-900/40 border-emerald-500/30">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
                        <Volume2 className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-bold text-white">Read to Me</p>
                        <p className="text-xs text-emerald-300">Natural AI voice reads the story!</p>
                      </div>
                      {!isReading ? (
                        <Button
                          onClick={readStory}
                          disabled={ttsLoading}
                          className="bg-emerald-500 hover:bg-emerald-600"
                          size="lg"
                          data-testid="read-to-me-btn"
                        >
                          {ttsLoading ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin mr-2" />
                              <span className="text-sm">Preparing audio...</span>
                            </>
                          ) : (
                            <>
                              <Play className="w-5 h-5 mr-2" />
                              Start
                            </>
                          )}
                        </Button>
                      ) : (
                        <div className="flex gap-2">
                          <Button
                            onClick={pauseReading}
                            variant="outline"
                            size="icon"
                            className="border-emerald-500 text-emerald-400"
                          >
                            <Pause className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={stopReading}
                            variant="outline"
                            size="icon"
                            className="border-red-500 text-red-400"
                          >
                            <VolumeX className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {/* Reading Progress */}
                    {(isReading || readingProgress > 0) && (
                      <div className="space-y-2">
                        <Progress value={readingProgress} className="h-2" />
                        <div className="flex justify-between text-xs text-slate-400">
                          <span>{isReading ? '🎧 Listening...' : readingProgress >= 100 ? '✅ Done!' : '⏸️ Paused'}</span>
                          <span>{Math.round(readingProgress)}%</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Voice & Speed Controls */}
                    <div className="mt-3 pt-3 border-t border-emerald-500/20">
                      <VoiceSelector
                        voice={selectedVoice}
                        onVoiceChange={setSelectedVoice}
                        speed={speechRate}
                        onSpeedChange={setSpeechRate}
                        theme="dark"
                        className="bg-slate-700/50 border-slate-600"
                      />
                    </div>
                  </CardContent>
                </Card>

                <div className="bg-slate-800/50 rounded-xl p-4">
                  <p className="text-sm text-slate-400 mb-2">Scripture Reference</p>
                  <p className="text-purple-300 font-medium">{weekData?.scripture}</p>
                </div>
                
                {/* Story Text */}
                <div className="bg-slate-800/50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm text-slate-400">The Story</p>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-purple-400 h-auto py-1"
                      onClick={() => speakText(storyContent.story.substring(0, 200))}
                    >
                      <Volume2 className="w-3 h-3 mr-1" />
                      Preview
                    </Button>
                  </div>
                  <p className="text-white leading-relaxed text-base">
                    {storyContent.story}
                  </p>
                </div>

                {/* Fun Fact */}
                <div className="bg-amber-500/20 border border-amber-500/30 rounded-xl p-4">
                  <p className="text-amber-300 font-medium mb-1 flex items-center gap-2">
                    <Star className="w-4 h-4" />
                    Fun Fact!
                  </p>
                  <p className="text-white text-sm">{storyContent.funFact}</p>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-amber-400 mt-2 h-auto py-1"
                    onClick={() => speakText(storyContent.funFact)}
                    disabled={shortTextSpeaking}
                  >
                    {shortTextSpeaking ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <Volume2 className="w-3 h-3 mr-1" />
                    )}
                    {shortTextSpeaking ? 'Reading...' : 'Read Fun Fact'}
                  </Button>
                </div>

                <div className="bg-purple-500/20 border border-purple-500/30 rounded-xl p-4">
                  <p className="text-purple-300 font-medium mb-1">Character Lesson</p>
                  <p className="text-white text-sm">{weekData?.character_lesson}</p>
                </div>
              </CardContent>
            </Card>

            <Button 
              onClick={() => {
                stopReading();
                setCurrentStep(1);
              }} 
              className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 py-6"
            >
              Next: Memory Verse <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        )}

        {/* Memory Verse Step */}
        {currentStep === 1 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            <Card className="bg-gradient-to-br from-amber-900/30 to-orange-900/30 border-amber-500/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Heart className="w-5 h-5 text-amber-400" />
                  Memory Verse
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center py-6">
                  <p className="text-amber-400 text-sm mb-4">{weekData?.scripture}</p>
                  <p className="text-2xl text-white font-medium leading-relaxed">
                    "{weekData?.verse}"
                  </p>
                </div>

                {/* Read Verse Controls */}
                <div className="grid grid-cols-2 gap-3">
                  <Button 
                    onClick={() => speakText(weekData?.verse)}
                    disabled={shortTextSpeaking}
                    className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/30"
                  >
                    {shortTextSpeaking ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Volume2 className="w-4 h-4 mr-2" />
                    )}
                    {shortTextSpeaking ? 'Reading...' : 'Listen'}
                  </Button>
                  <Button 
                    onClick={() => {
                      speakText(weekData?.verse);
                      setTimeout(() => toast.info('Now you say it!'), 4000);
                    }}
                    disabled={shortTextSpeaking}
                    className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/30"
                  >
                    {shortTextSpeaking ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RotateCcw className="w-4 h-4 mr-2" />
                    )}
                    Listen & Repeat
                  </Button>
                </div>

                <div className="bg-slate-800/50 rounded-xl p-4">
                  <p className="text-sm text-slate-400 mb-3">Scripture Ladder - Day 1: Hear</p>
                  <div className="grid grid-cols-7 gap-1">
                    {['Hear', 'Repeat', 'Write', 'Explain', 'Recite', 'Teach', 'Group'].map((day, idx) => (
                      <div 
                        key={idx} 
                        className={`text-center py-2 rounded text-xs ${
                          idx === 0 ? 'bg-purple-500 text-white' : 'bg-slate-700 text-slate-400'
                        }`}
                      >
                        {day[0]}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Speed Control for verse */}
                <div className="bg-slate-800/30 rounded-xl p-3">
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-400">Read Speed:</span>
                    <div className="flex-1">
                      <Slider
                        value={[speechRate * 100]}
                        onValueChange={(val) => setSpeechRate(val[0] / 100)}
                        min={50}
                        max={120}
                        step={5}
                      />
                    </div>
                    <span className="text-xs text-amber-400">{Math.round(speechRate * 100)}%</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setCurrentStep(0)} className="flex-1 border-slate-700 text-white">
                Back
              </Button>
              <Button onClick={() => setCurrentStep(2)} className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600">
                Next: Activity <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* Activity Step */}
        {currentStep === 2 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            {showInteractiveWorksheet ? (
              /* Interactive Worksheet Mode */
              <div className="space-y-3">
                <Button variant="ghost" size="sm" onClick={() => setShowInteractiveWorksheet(false)}
                  className="text-slate-400 hover:text-white">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back to Activity Menu
                </Button>
                <InteractiveWorksheet
                  weekNumber={weekNum}
                  childId={childId}
                  api={apiHelper}
                  onComplete={(results) => {
                    setWorksheeetCompleted(true);
                    setShowInteractiveWorksheet(false);
                    toast.success(`Worksheet complete! +${results.xp_earned} XP`);
                  }}
                />
              </div>
            ) : (
              /* Activity Menu */
              <>
                <Card className="bg-gradient-to-br from-pink-900/30 to-purple-900/30 border-pink-500/30">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Palette className="w-5 h-5 text-pink-400" />
                      Activity Time!
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-slate-800/50 rounded-xl p-6 text-center">
                      <div className="text-6xl mb-4">🎨</div>
                      <h3 className="text-xl font-bold text-white mb-2">{weekData?.activity}</h3>
                      <p className="text-slate-400">
                        This activity helps you remember the lesson about {weekData?.topic}!
                      </p>
                    </div>

                    {/* Interactive Worksheet - Primary CTA */}
                    <Button
                      onClick={() => setShowInteractiveWorksheet(true)}
                      data-testid="start-interactive-worksheet-btn"
                      className="w-full h-auto py-4 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 flex items-center justify-center gap-3"
                    >
                      <Gamepad2 className="w-6 h-6" />
                      <div className="text-left">
                        <p className="font-bold text-sm">Complete Interactive Worksheet</p>
                        <p className="text-xs text-indigo-200">Quiz, word search, drawing & more - earn XP!</p>
                      </div>
                      {worksheetCompleted && <Check className="w-5 h-5 text-emerald-300 ml-auto" />}
                    </Button>

                    <div className="grid grid-cols-2 gap-3">
                      <Button 
                        variant="outline" 
                        className="h-auto py-4 flex flex-col items-center gap-2 border-slate-700 text-white hover:bg-emerald-900/30 hover:border-emerald-500/50"
                        onClick={downloadWorksheet}
                        disabled={downloadingWorksheet}
                        data-testid="print-worksheet-btn"
                      >
                        {downloadingWorksheet ? (
                          <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
                        ) : (
                          <Printer className="w-6 h-6 text-emerald-400" />
                        )}
                        <span className="text-sm">{downloadingWorksheet ? 'Downloading...' : 'Print Worksheet'}</span>
                      </Button>
                      <Button 
                        variant="outline" 
                        className="h-auto py-4 flex flex-col items-center gap-2 border-slate-700 text-white hover:bg-blue-900/30 hover:border-blue-500/50"
                        onClick={() => setShowVideo(true)}
                        data-testid="watch-video-btn"
                      >
                        <Play className="w-6 h-6 text-blue-400" />
                        <span className="text-sm">Watch Video</span>
                      </Button>
                    </div>

                    {/* Video Player */}
                    {showVideo && activityVideo && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-4"
                      >
                        <Card className="bg-slate-800/50 border-blue-500/30 overflow-hidden">
                          <div className="aspect-video w-full">
                            <iframe
                              width="100%"
                              height="100%"
                              src={`https://www.youtube.com/embed/${activityVideo.id}?rel=0&modestbranding=1`}
                              title={activityVideo.title}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              allowFullScreen
                            />
                          </div>
                          <div className="p-3 flex items-center justify-between">
                            <div>
                              <p className="text-white font-medium text-sm">{activityVideo.title}</p>
                              <p className="text-slate-400 text-xs">Week {weekNum} Activity Video</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowVideo(false)}
                              className="text-slate-400 hover:text-white"
                            >
                              Close
                            </Button>
                          </div>
                        </Card>
                      </motion.div>
                    )}
                  </CardContent>
                </Card>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setCurrentStep(1)} className="flex-1 border-slate-700 text-white">
                    Back
                  </Button>
                  <Button onClick={() => setCurrentStep(3)} className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600">
                    Next: Wrap Up <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </>
            )}
          </motion.div>
        )}

        {/* Character Lesson Step */}
        {currentStep === 3 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            <Card className="bg-gradient-to-br from-emerald-900/30 to-teal-900/30 border-emerald-500/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Star className="w-5 h-5 text-emerald-400" />
                  Character Lesson
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-slate-800/50 rounded-xl p-6 text-center">
                  <div className="text-5xl mb-4">💡</div>
                  <h3 className="text-lg font-bold text-white mb-4">{weekData?.character_lesson}</h3>
                  <p className="text-slate-300">
                    This week's theme is <span className="text-emerald-400 font-medium">{lesson?.character_focus}</span>.
                  </p>
                </div>

                <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-xl p-4">
                  <p className="text-emerald-300 font-medium mb-2">Discussion Questions:</p>
                  <ul className="space-y-2 text-slate-300 text-sm">
                    <li>• How can you practice {lesson?.character_focus?.toLowerCase()} this week?</li>
                    <li>• What did you learn from {weekData?.topic}?</li>
                    <li>• How does this story help you know God better?</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setCurrentStep(2)} className="flex-1 border-slate-700 text-white">
                Back
              </Button>
              <Button onClick={() => setCurrentStep(4)} className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600">
                Finish Lesson <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* Complete Step */}
        {currentStep === 4 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-4"
          >
            <Card className="bg-gradient-to-br from-amber-900/30 to-orange-900/30 border-amber-500/30 text-center">
              <CardContent className="p-8">
                <div className="text-6xl mb-4">🎉</div>
                <h2 className="text-2xl font-bold text-white mb-2">Amazing Job!</h2>
                <p className="text-slate-300 mb-6">
                  You completed Week {weekNumber}: {weekData?.topic}
                </p>
                
                <div className="bg-slate-800/50 rounded-xl p-4 mb-6">
                  <p className="text-amber-400 text-sm mb-2">You earned:</p>
                  <p className="text-3xl font-bold text-amber-400">+25 XP</p>
                </div>

                <Button
                  onClick={handleComplete}
                  disabled={completing}
                  className="w-full bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 hover:from-amber-500 hover:to-orange-600 py-6 text-lg"
                >
                  {completing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Trophy className="w-5 h-5 mr-2" />
                      Complete & Continue
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ChristianLesson;
